import {
  DeliveryReportAction,
  ForwardRecipientKind,
} from "@lytra/frontend-common/__generated__/graphql";
import type { DeliveryProblem } from "./DeliveryProblemNotice";
import type { TicketMessage } from "./noteThreads";
import { personName } from "./ticketHelpers";

/** One recipient as the ticket query stores it on a send (#717, D-07). */
export type ForwardRecipient = TicketMessage["forwardRecipients"][number];

/** The entry shows this many recipients before "{n} weitere anzeigen" (UI-SPEC 4). */
export const RECIPIENT_LIMIT = 5;

const sameId = (a?: string | null, b?: string | null): boolean =>
  Boolean(a && b) && a!.toLowerCase() === b!.toLowerCase();

/** Whether `recipient` is the viewer: a Lytra recipient whose user is the signed-in user. */
export function isViewerRecipient(
  recipient: ForwardRecipient,
  viewerId?: string,
): boolean {
  return (
    recipient.kind === ForwardRecipientKind.Lytra &&
    sameId(recipient.userId, viewerId)
  );
}

/**
 * The recipients to show: all of them when expanded or when they fit, else the first five
 * in the order they were added, with the viewer's own row always among them (it takes the
 * fifth place when it was added later).
 */
export function visibleRecipients(
  recipients: readonly ForwardRecipient[],
  viewerId: string | undefined,
  expanded: boolean,
): ForwardRecipient[] {
  if (expanded || recipients.length <= RECIPIENT_LIMIT) return [...recipients];
  const head = recipients.slice(0, RECIPIENT_LIMIT);
  if (head.some((recipient) => isViewerRecipient(recipient, viewerId))) {
    return head;
  }
  const viewer = recipients.find((recipient) =>
    isViewerRecipient(recipient, viewerId),
  );
  return viewer ? [...head.slice(0, RECIPIENT_LIMIT - 1), viewer] : head;
}

/**
 * What a Lytra recipient is called: the colleague's current name, else the name stored at
 * send time (a removed colleague stays readable), never blank.
 */
export function lytraRecipientName(
  recipient: ForwardRecipient,
  agentsById: ReadonlyMap<
    string,
    { givenName?: string | null; familyName?: string | null }
  >,
  fallback: string,
): string {
  const current = recipient.userId
    ? personName(agentsById.get(String(recipient.userId)))
    : null;
  return current ?? (recipient.label.trim() || fallback);
}

/** The reported failures (not delays) for one address, matched case-insensitively. */
export function failedProblemsFor(
  problems: readonly DeliveryProblem[],
  address: string | null | undefined,
): DeliveryProblem[] {
  if (!address) return [];
  const wanted = address.toLowerCase();
  return problems.filter(
    (problem) =>
      problem.action === DeliveryReportAction.Failed &&
      problem.recipient.toLowerCase() === wanted,
  );
}

/** Reported failures for an address that is not one of the send's mail recipients. */
export function unmatchedFailedProblems(
  problems: readonly DeliveryProblem[],
  recipients: readonly ForwardRecipient[],
): DeliveryProblem[] {
  const addresses = new Set(
    recipients
      .filter((recipient) => recipient.kind === ForwardRecipientKind.Mail)
      .map((recipient) => (recipient.address ?? "").toLowerCase()),
  );
  return problems.filter(
    (problem) =>
      problem.action === DeliveryReportAction.Failed &&
      !addresses.has(problem.recipient.toLowerCase()),
  );
}
